'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'clsx';
import { HTMLAttributes, useLayoutEffect, useRef, useState } from 'react';

import { Divider } from '../../divider/Divider';
import { TabItem } from '../../tabItem/TabItem';

const barTabsVariants = cva('flex relative gap-tiny-large', {
  variants: {
    variant: { segment: 'bg-neutral-background-low' },
    size: {
      medium: ''
    }
  },
  defaultVariants: {
    size: 'medium'
  }
});

export interface BarTabsProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof barTabsVariants> {
  tabsItem: string[];
  selectedIndex: number;
  selectTab: (index: number) => void;
  isDividerVisible?: boolean;
  fullWidth?: boolean;
}

export const BarTabs = ({ size = 'medium', tabsItem, selectedIndex, selectTab, isDividerVisible = true, fullWidth = false }: BarTabsProps) => {
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabItemsRef = useRef<null[] | HTMLDivElement[]>([]);

  const [selectedTabWidth, setSelectedTabWidth] = useState<number>(0);
  const [selectedTabTransform, setSelectedTabTransform] = useState<number>(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const tabsWrapper = tabsRef.current;
    const selectedTab = tabItemsRef.current[selectedIndex];

    if (selectedTab && tabsRef.current && tabItemsRef.current) {
      setSelectedTabWidth(selectedTab.offsetWidth);
      setSelectedTabTransform(selectedTab.getBoundingClientRect().x - tabsRef.current?.getBoundingClientRect().x);
    }

    if (tabsWrapper && selectedTab && container) {
      const tabsWrapperRect = tabsWrapper.getBoundingClientRect();
      const tabRect = selectedTab.getBoundingClientRect();

      const scrollLeft = tabsWrapper.scrollLeft + (tabRect.left + tabRect.width / 2 - (tabsWrapperRect.left + container?.getBoundingClientRect().width / 2));

      container?.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [selectedIndex, tabsItem.length]);

  if (tabsItem.length === 0) {
    console.error('tabsItem must be at least 1 tab');
    return null;
  }

  return (
    <div
      className={'w-full bg-neutral-background-main'}
      style={{
        maskImage: 'linear-gradient(90deg, #0000, #000 20px, #000 calc(100% - 20px), #0000)'
      }}
    >
      <div ref={containerRef} className={'scrollbar-hide container-padding-medium relative h-fit overflow-x-scroll'}>
        <div ref={tabsRef} className={cn(barTabsVariants({ size }), fullWidth ? 'w-full' : 'w-fit')}>
          <TabItem
            variant={'bar'}
            size={size}
            label={''}
            className={'!absolute bottom-0 top-0 bg-white transition-all duration-300 will-change-transform'}
            style={{
              transform: `translate3d(${selectedTabTransform}px, 0, 0)`,
              width: selectedTabWidth
            }}
            selected={true}
          />
          {tabsItem.map((label, index) => (
            <TabItem
              ref={(el) => {
                tabItemsRef.current[index] = el;
              }}
              variant={'bar'}
              size={size}
              className={cn('z-10', fullWidth ? 'flex-1' : 'basis-0')}
              label={label}
              key={index}
              selected={index === selectedIndex}
              hiddenActiveBar
              onClick={() => {
                selectTab(index);
              }}
            />
          ))}
        </div>
      </div>
      {isDividerVisible && (
        <div className="container-padding-medium">
          <Divider />
        </div>
      )}
    </div>
  );
};
